import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, XCircle, AlertTriangle, 
  Brain, CheckCircle, TrendingUp, 
  Database, ShieldCheck, FileSearch,
  History, Sparkles, ArrowRight, ScanLine, X
} from 'lucide-react';
import { Badge } from '../../ui/badge';

const AlainaSlide = () => {
    const [scannedCount, setScannedCount] = useState(0);
    const [currentDenial, setCurrentDenial] = useState(0);

    // Simulate scanning historical data
    useEffect(() => {
        const interval = setInterval(() => {
            setScannedCount(prev => prev < 15420 ? prev + 123 : 15420);
        }, 50);
        return () => clearInterval(interval);
    }, []);

    // Cycle through denial examples
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentDenial(prev => (prev + 1) % denialExamples.length);
        }, 4000);
        return () => clearInterval(interval);
    }, []);

    return (
    <div className="h-full w-full bg-slate-900 flex items-center justify-center p-12 relative overflow-hidden text-white">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/10 backdrop-blur-md p-2 pr-6 rounded-full border border-white/10 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-700 shadow-sm bg-indigo-500/20 flex items-center justify-center">
                 <img src="https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png" alt="Alaina" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-white leading-none">Alayna</h3>
                <p className="text-xs text-indigo-400 font-bold uppercase tracking-wide">AI Claim Scrubber</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-12 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-8">
                <div className="space-y-4">
                    <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                        Ariel comes with a teammate, <span className="text-indigo-400">Alayna</span>, the AI claim scrubber.
                    </h2>
                    
                    <p className="text-lg text-slate-300 leading-relaxed">
                        While humans can make mistakes, Alayna analyzes thousands of historical data points to understand exactly why claims get denied by specific payers. She preemptively fixes issues before submission to maximize your first-pass collection rate.
                    </p>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-indigo-500/5 group-hover:bg-indigo-500/10 transition-colors" />
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                    <Database className="w-5 h-5 text-indigo-400" />
                                    <h4 className="font-bold">Historical Data Scanned</h4>
                                </div>
                                <span className="text-xl font-mono text-indigo-300">
                                    {scannedCount.toLocaleString()}
                                </span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <motion.div 
                                    className="h-full bg-indigo-500"
                                    initial={{ width: "0%" }}
                                    animate={{ width: "100%" }}
                                    transition={{ duration: 2, ease: "easeOut" }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                        <div className="flex items-center gap-3 mb-2">
                            <ShieldCheck className="w-5 h-5 text-green-400" />
                            <h4 className="font-bold">Preemptive Scrubbing</h4>
                        </div>
                        <p className="text-sm text-slate-400">Corrects errors before the payer ever sees them.</p>
                    </div>
                </div>
            </div>

            {/* Right Side: Animated Visualization */}
            <div className="w-2/3 h-full flex items-center justify-center">
                <div className="w-full h-[600px] relative bg-slate-950 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden flex flex-col">
                    
                    {/* Header */}
                    <div className="h-14 bg-slate-900 border-b border-slate-800 flex items-center px-6 justify-between flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-bold text-slate-200">Alayna Intelligent Scrubber</span>
                        </div>
                        <Badge variant="outline" className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300 font-mono text-xs">
                             Analyzing Payer Rules
                        </Badge>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 p-8 flex flex-col gap-8 relative">
                         {/* Background Grid */}
                         <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:2rem_2rem]" />

                         {/* Central Animation */}
                         <div className="flex-1 flex items-center justify-center relative">
                            
                            {/* Payer Rules Stream (Left) */}
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-64 h-full flex flex-col justify-center gap-4 mask-gradient-r">
                                {[...Array(5)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ x: -50, opacity: 0 }}
                                        animate={{ x: 0, opacity: [0, 1, 0] }}
                                        transition={{ 
                                            duration: 3, 
                                            repeat: Infinity, 
                                            delay: i * 0.5,
                                            ease: "linear" 
                                        }}
                                        className="bg-slate-800/50 p-3 rounded-lg border border-slate-700 text-xs text-slate-400 font-mono"
                                    >
                                        &lt;Rule id="PAY-{1000+i}" /&gt;
                                    </motion.div>
                                ))}
                            </div>

                            {/* Alaina Core (Center) */}
                            <div className="relative z-20">
                                <div className="w-32 h-32 relative">
                                    {/* Scanning rings */}
                                    <motion.div 
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                        className="absolute inset-0 rounded-full border border-indigo-500/30 border-dashed"
                                    />
                                    <motion.div 
                                        animate={{ rotate: -360 }}
                                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                                        className="absolute inset-2 rounded-full border border-blue-500/30 border-dashed"
                                    />
                                    
                                    {/* Core Image */}
                                    <div className="absolute inset-4 rounded-full overflow-hidden border-2 border-indigo-400 shadow-[0_0_30px_rgba(99,102,241,0.4)]">
                                        <img src="https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png" alt="Alaina" className="w-full h-full object-cover" />
                                    </div>
                                </div>
                            </div>

                            {/* Denial Prevention (Right) */}
                            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[340px] z-20">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentDenial}
                                        initial={{ x: 50, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        exit={{ x: 50, opacity: 0 }}
                                        transition={{ duration: 0.5 }}
                                        className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-2xl"
                                    >
                                        <div className="bg-red-500/10 border-b border-red-500/20 p-3 flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-red-400 text-sm font-bold">
                                                <AlertTriangle className="w-4 h-4" />
                                                Potential Denial Detected
                                            </div>
                                            <span className="text-[10px] text-slate-500 font-mono">{denialExamples[currentDenial].code}</span>
                                        </div>
                                        
                                        <div className="p-4 space-y-4">
                                            <div>
                                                <div className="text-xs text-slate-500 uppercase font-bold mb-1">Issue</div>
                                                <p className="text-sm text-slate-300">{denialExamples[currentDenial].issue}</p>
                                            </div>

                                            <div className="flex justify-center">
                                                <ArrowRight className="w-5 h-5 text-slate-600 rotate-90" />
                                            </div>

                                            <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                                                <div className="flex items-center gap-2 text-green-400 text-xs font-bold mb-1">
                                                    <CheckCircle className="w-3.5 h-3.5" />
                                                    Alayna's Fix
                                                </div>
                                                <p className="text-sm text-green-200">{denialExamples[currentDenial].fix}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                         </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
  );
};

const denialExamples = [
    {
        code: "D4341",
        issue: "Frequency limitation on Periodontal Scaling (4 quadrants same day).",
        fix: "Auto-attached narrative & periodontal charting to justify medical necessity."
    },
    {
        code: "D2740",
        issue: "Missing pre-op X-ray for Crown placement.",
        fix: "Located and attached PA Bitewing from 10/12/2023 history."
    },
    {
        code: "D0150",
        issue: "Comprehensive Exam bundled with recent D0120.",
        fix: "Flagged for downgrade review; switched to D0120 to match payer allowance."
    },
    {
        code: "D2391",
        issue: "Invalid surface code for Posterior Composite.",
        fix: "Corrected surface code based on tooth number chart logic."
    }
];

export default AlainaSlide;
